import { useEffect, useState, useRef } from "react";
import { useScroll } from "../contexts/ScrollContext";
import { useLocation } from "react-router-dom";

export default function useMenuOpacity() {
  const { scrollPosition, isRestoringScroll } = useScroll();
  const location = useLocation();
  const [opacity, setOpacity] = useState(1);
  const prevScrollPos = useRef(0);
  const targetOpacityRef = useRef(1);
  const animationRef = useRef(null);

  const animateOpacity = () => {
    setOpacity((current) => {
      const target = targetOpacityRef.current;
      const diff = target - current;

      if (Math.abs(diff) < 0.01) return target;

      const next = current + diff * 0.1;
      animationRef.current = requestAnimationFrame(animateOpacity);
      return next;
    });
  };

  useEffect(() => {
    setOpacity(1);
    targetOpacityRef.current = 1;
    prevScrollPos.current = 0;
  }, [location.pathname]);

  useEffect(() => {
    if (isRestoringScroll) {
      const newOpacity = Math.max(0.4, 1 - scrollPosition / 500);
      setOpacity(newOpacity);
      targetOpacityRef.current = newOpacity;
      return;
    }

    const scrollDelta = scrollPosition - prevScrollPos.current;
    prevScrollPos.current = scrollPosition;

    let target = targetOpacityRef.current;

    if (scrollDelta > 5) {
      // Scrolling down
      target = Math.max(0.3, opacity - 0.2);
      const positionBasedOpacity = Math.max(0.4, 1 - scrollPosition / 500);
      target = Math.min(target, positionBasedOpacity);
    } else if (scrollDelta < -5) {
      // Scrolling up
      target = 1; // Fully restore
    }

    targetOpacityRef.current = target;

    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(animateOpacity);

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [scrollPosition, isRestoringScroll]);

  return opacity;
}
